import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownTextProps {
  children: string;
  /**
   * Compact mode tightens vertical rhythm for narrow surfaces like the
   * analytics drawer and the mobile bottom sheet. Default (false) matches
   * the looser /chat-tab bubble spacing.
   */
  compact?: boolean;
}

/**
 * Renders AI assistant message content as styled markdown.
 *
 * The /chat tab has its own inline ReactMarkdown config with looser
 * (rounded) spacing; this component exists so the analytics drawer,
 * mobile bottom sheet, and any future chat surface can share one set
 * of component overrides tuned for dense monochrome rendering.
 *
 * Uses `remark-gfm` so tables, strikethrough, task lists, and auto-links
 * work without additional plugins. Raw HTML is intentionally NOT enabled
 * — `react-markdown` ignores it by default, which is the safe path.
 */
export const MarkdownText: React.FC<MarkdownTextProps> = ({ children, compact = false }) => {
  const headingMargin = compact ? 'mt-3 mb-1.5' : 'mt-4 mb-2';
  const blockMargin = compact ? 'mb-2' : 'mb-3';
  const listIndent = compact ? 'pl-4' : 'pl-5';

  return (
    <div className="text-sm text-neutral-900 dark:text-neutral-100 leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className={`text-base font-bold text-neutral-900 dark:text-neutral-100 ${headingMargin}`}>{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className={`text-sm font-bold text-neutral-900 dark:text-neutral-100 uppercase tracking-wide ${headingMargin}`}>{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className={`text-sm font-semibold text-neutral-900 dark:text-neutral-100 ${headingMargin}`}>{children}</h3>
          ),
          p: ({ children }) => (
            <p className={`text-neutral-900 dark:text-neutral-100 ${blockMargin} leading-relaxed`}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul className={`list-disc ${listIndent} ${blockMargin} space-y-1 marker:text-neutral-500`}>
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className={`list-decimal ${listIndent} ${blockMargin} space-y-1 marker:text-neutral-500`}>
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="text-neutral-900 dark:text-neutral-100">{children}</li>,
          strong: ({ children }) => (
            <strong className="font-semibold text-neutral-900 dark:text-neutral-50">{children}</strong>
          ),
          em: ({ children }) => <em className="italic text-neutral-700 dark:text-neutral-200">{children}</em>,
          // Inline vs fenced distinction: react-markdown v10 renders fenced
          // blocks as <pre><code class="language-xxx">...</code></pre>.
          // Only the inline variant (no className) gets the chip treatment;
          // the fenced path lets the <pre> override own padding/background so
          // the two don't stack on top of each other.
          code: ({ className, children }) => {
            if (className) {
              return <code className={className}>{children}</code>;
            }
            return (
              <code className="px-1 py-0.5 bg-neutral-200 text-neutral-900 dark:bg-white/10 dark:text-neutral-100 text-xs font-mono">
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className={`p-3 bg-neutral-100 border border-neutral-200 text-neutral-900 dark:bg-black/40 dark:border-white/10 dark:text-neutral-100 overflow-x-auto text-xs ${blockMargin}`}>
              {children}
            </pre>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-neutral-900 hover:text-black dark:text-neutral-100 dark:hover:text-white"
            >
              {children}
            </a>
          ),
          // `remark-gfm` emits <hr> for `---` between sections — give it a
          // subtle divider instead of the default default-HTML line.
          hr: () => <hr className={`border-0 border-t border-neutral-200 dark:border-white/15 ${blockMargin} mt-3`} />,
          // Tables (GFM). Keep them compact; the drawer column is narrow.
          table: ({ children }) => (
            <div className={`${blockMargin} overflow-x-auto`}>
              <table className="text-xs border-collapse">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="text-left font-semibold px-2 py-1 border border-neutral-300 bg-neutral-100 dark:border-white/15 dark:bg-white/5">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-2 py-1 border border-neutral-200 dark:border-white/10">{children}</td>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};
